import type {
  CommendaAiPageContext,
  CommendaAiPageItem,
  CommendaAiPageSection,
} from '@commenda/api/commenda-ai/types';

import type { ChatPanelPageDescriptor } from '../types/types';

/** What the DOM pass collects before caps and trimming are applied. */
interface RawPageItem {
  type: string;
  id?: string;
  href?: string;
  text: string;
}

interface RawPageSection {
  name: string;
  text?: string;
  items: RawPageItem[];
}

interface RawPageHarvest {
  sections: RawPageSection[];
  visibleText: string | null;
  isLoading: boolean;
}

/** Reads the page the chat panel is attached to and shapes it into the payload the assistant
 * receives: named sections from `data-ai-section`, their items from `data-ai-item` with the record
 * ids stamped by `data-ai-id`, and the visible text of a page that has neither.
 *
 * Caps mirror the backend DTO's bounds exactly. Anything over them rejects the whole message, so
 * this cuts rather than trusts, and marks every cut so the assistant knows the page went on. */
export class PageContextHelper {
  static readonly MAX_SECTIONS = 20;

  static readonly MAX_ITEMS_PER_SECTION = 50;

  static readonly MAX_ITEM_TEXT = 300;

  static readonly MAX_SECTION_TEXT = 2000;

  static readonly MAX_VISIBLE_TEXT = 8000;

  static readonly MAX_SCREEN = 120;

  static readonly MAX_LABEL = 160;

  static readonly MAX_NAME = 120;

  static readonly MAX_TYPE = 60;

  static readonly MAX_ID = 200;

  static readonly MAX_HREF = 300;

  static readonly TRUNCATION_MARK = '…';

  /** Stands in for the sections past the cap, so the count of what was cut still reaches the
   * assistant. */
  static readonly OVERFLOW_SECTION_NAME = 'Further sections';

  /** Never read as content: structural, scripted, or the chat panel's own column. */
  static readonly EXCLUDED_TAGS: ReadonlySet<string> = new Set([
    'ASIDE',
    'SCRIPT',
    'STYLE',
    'NOSCRIPT',
    'SVG',
    'IFRAME',
    'CANVAS',
  ]);

  /** Tags whose text reads as its own line rather than running into a neighbour. */
  static readonly BLOCK_TAGS: ReadonlySet<string> = new Set([
    'DIV',
    'P',
    'SECTION',
    'ARTICLE',
    'HEADER',
    'FOOTER',
    'TABLE',
    'THEAD',
    'TBODY',
    'TR',
    'UL',
    'OL',
    'LI',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
  ]);

  /** Reads the page as it stands right now. Scoped to the layout's content card, which leaves out
   * the chat panel, the navigation and any page-owned aside by construction. */
  static harvest(
    descriptor: ChatPanelPageDescriptor | null
  ): CommendaAiPageContext | null {
    if (!descriptor) return null;
    if (typeof document === 'undefined')
      return PageContextHelper.buildPageContext(descriptor, null);

    const root = document.querySelector('[data-ai-main]');

    if (!root) return PageContextHelper.buildPageContext(descriptor, null);

    const sections = PageContextHelper.collectSections(root);
    const visibleText =
      sections.length === 0
        ? PageContextHelper.normalizeBlockText(
            PageContextHelper.collectText(root)
          )
        : null;

    return PageContextHelper.buildPageContext(descriptor, {
      sections,
      visibleText,
      isLoading: PageContextHelper.isLoading(root),
    });
  }

  /** A page mid-render is reported as loading rather than asserted as empty. Only a region that
   * declares itself busy counts; a pulsing placeholder deep in one widget says nothing about the
   * rest of the page. */
  static isLoading(root: Element): boolean {
    return (
      root.getAttribute('aria-busy') === 'true' ||
      root.querySelector('[aria-busy="true"]') !== null
    );
  }

  /** Collapses runs of spaces and blank lines; each line arrives trimmed. */
  static normalizeBlockText(text: string): string {
    return text
      .split('\n')
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n');
  }

  /** One line for an item: inner line breaks become separators. */
  static normalizeInlineText(text: string): string {
    return PageContextHelper.normalizeBlockText(text).replace(/\n/g, ' · ');
  }

  /** Cuts to `max` characters, the last of which marks the cut. */
  static truncate(text: string, max: number): string {
    return text.length <= max
      ? text
      : `${text.slice(0, max - PageContextHelper.TRUNCATION_MARK.length)}${PageContextHelper.TRUNCATION_MARK}`;
  }

  static buildPageContext(
    descriptor: ChatPanelPageDescriptor | null,
    harvest: RawPageHarvest | null
  ): CommendaAiPageContext | null {
    // An unmapped route describes nothing rather than guessing.
    if (!descriptor) return null;

    const context: CommendaAiPageContext = {
      screen: PageContextHelper.truncate(
        descriptor.screen,
        PageContextHelper.MAX_SCREEN
      ),
      label: PageContextHelper.truncate(
        descriptor.label,
        PageContextHelper.MAX_LABEL
      ),
    };

    if (Object.keys(descriptor.ids).length > 0) context.ids = descriptor.ids;

    if (!harvest) return context;

    if (harvest.isLoading) context.isLoading = true;

    const sections = PageContextHelper.buildSections(
      harvest.sections.filter((section) => section.name.trim().length > 0)
    );

    if (sections.length > 0) {
      context.sections = sections;
    } else if (harvest.visibleText) {
      context.visibleText = PageContextHelper.truncate(
        harvest.visibleText,
        PageContextHelper.MAX_VISIBLE_TEXT
      );
    }

    return context;
  }

  /** Past the cap, the last slot becomes a note of how many sections were cut. */
  private static buildSections(
    sections: RawPageSection[]
  ): CommendaAiPageSection[] {
    if (sections.length <= PageContextHelper.MAX_SECTIONS)
      return sections.map((section) => PageContextHelper.buildSection(section));

    const kept = PageContextHelper.MAX_SECTIONS - 1;

    return [
      ...sections
        .slice(0, kept)
        .map((section) => PageContextHelper.buildSection(section)),
      {
        name: PageContextHelper.OVERFLOW_SECTION_NAME,
        text: `${sections.length - kept} more sections on this page are not shown`,
      },
    ];
  }

  private static buildSection(section: RawPageSection): CommendaAiPageSection {
    const built: CommendaAiPageSection = {
      name: PageContextHelper.truncate(
        section.name.trim(),
        PageContextHelper.MAX_NAME
      ),
    };

    if (section.items.length > 0) {
      built.items = section.items
        .slice(0, PageContextHelper.MAX_ITEMS_PER_SECTION)
        .map((item) => PageContextHelper.buildItem(item));

      const hidden =
        section.items.length - PageContextHelper.MAX_ITEMS_PER_SECTION;

      if (hidden > 0) built.truncatedCount = hidden;

      return built;
    }

    if (section.text)
      built.text = PageContextHelper.truncate(
        section.text,
        PageContextHelper.MAX_SECTION_TEXT
      );

    return built;
  }

  private static buildItem(item: RawPageItem): CommendaAiPageItem {
    const built: CommendaAiPageItem = {
      type: PageContextHelper.truncate(
        item.type.trim(),
        PageContextHelper.MAX_TYPE
      ),
      text: PageContextHelper.truncate(
        item.text,
        PageContextHelper.MAX_ITEM_TEXT
      ),
    };

    if (item.id)
      built.id = PageContextHelper.truncate(item.id, PageContextHelper.MAX_ID);

    if (item.href)
      built.href = PageContextHelper.truncate(
        item.href,
        PageContextHelper.MAX_HREF
      );

    return built;
  }

  /** What the user cannot see is not page content: collapsed panels, inactive tabs and anything
   * styled away, not just the `hidden` attribute. */
  private static isHidden(element: Element): boolean {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden) return true;

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);

    return (
      style?.display === 'none' ||
      style?.visibility === 'hidden' ||
      style?.visibility === 'collapse'
    );
  }

  private static isExcluded(element: Element): boolean {
    return (
      PageContextHelper.EXCLUDED_TAGS.has(element.tagName) ||
      element.hasAttribute('data-ai-ignore') ||
      element.getAttribute('aria-hidden') === 'true' ||
      PageContextHelper.isHidden(element)
    );
  }

  /** Rendered text of a subtree without innerText's layout pass: text nodes joined, block-level
   * children on their own lines, excluded subtrees skipped entirely. */
  private static collectText(element: Element): string {
    if (PageContextHelper.isExcluded(element)) return '';

    let out = '';

    element.childNodes.forEach((node) => {
      if (node instanceof Text) {
        out += ` ${node.textContent ?? ''}`;

        return;
      }

      if (!(node instanceof Element)) return;

      const text = PageContextHelper.collectText(node);

      if (text.trim())
        out += PageContextHelper.BLOCK_TAGS.has(node.tagName)
          ? `\n${text}\n`
          : ` ${text}`;
    });

    return out;
  }

  /** Anything under an ignored or hidden ancestor is left out, even when annotated. */
  private static isReachable(element: Element, root: Element): boolean {
    if (element.closest('[data-ai-ignore]')) return false;

    let current: Element | null = element;

    while (current && current !== root) {
      if (PageContextHelper.isHidden(current)) return false;
      current = current.parentElement;
    }

    return true;
  }

  private static collectSections(root: Element): RawPageSection[] {
    const sectionElements = Array.from(
      root.querySelectorAll('[data-ai-section]')
    ).filter((element) => PageContextHelper.isReachable(element, root));

    return sectionElements.map((sectionElement) => {
      const itemElements = Array.from(
        sectionElement.querySelectorAll('[data-ai-item]')
      ).filter(
        (item) =>
          item.closest('[data-ai-section]') === sectionElement &&
          PageContextHelper.isReachable(item, sectionElement)
      );

      const items: RawPageItem[] = itemElements.map((item) => ({
        type: item.getAttribute('data-ai-item') ?? '',
        id: item.getAttribute('data-ai-id') ?? undefined,
        // Rows carry their record id in their link as often as in an attribute.
        href:
          item.closest('a[href]')?.getAttribute('href') ??
          item.querySelector('a[href]')?.getAttribute('href') ??
          undefined,
        text: PageContextHelper.normalizeInlineText(
          PageContextHelper.collectText(item)
        ),
      }));

      return {
        name: sectionElement.getAttribute('data-ai-section') ?? '',
        text:
          items.length === 0
            ? PageContextHelper.normalizeBlockText(
                PageContextHelper.collectText(sectionElement)
              )
            : undefined,
        items,
      };
    });
  }
}
